import React from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/pro-solid-svg-icons";
import { faCircleCheck, faInfoCircle, faTimes, faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";
import Spin from "components/spin/spin";

import { ButtonNew as Button } from "components/buttonNew";

import "./callout.scss";
import Markdown from "components/markdown/Markdown";

export type CalloutProps = {
  type: NoticeType;
  title: string;
  content?: string | JSX.Element;
  description?: string | JSX.Element;
  actions?: NoticeActions;
  /** Displays X in top-right corner. */
  onClose?: () => void;
  /** Displays Save button to the slight left of onCLose position */
  onSave?: () => void;
  /** Overrides default styling:
   * 1) display ICON to be strictly info circle
   * 2) onClose button becomes 'DISMISS'
   * 3) disables border
   */
  toast?: boolean;
  markdown?: string;
  loading?: boolean;
  iconAlignTop?: boolean;
  borderless?: boolean;
  hideType?: boolean;
};

type ActionClick = {
  label?: string;
  onClick: () => void;
  buttonType?: "primary" | "secondary" | "error" | "tertiary";
};

/** Primary action is given bolded text + right-arrow Icon */
type NoticeActions = {
  primary?: ActionClick;
  secondary?: ActionClick;
};

export type NoticeType = "primary" | "info" | "success" | "warning" | "error" | "alerts";

export default function Callout(props: CalloutProps) {
  const {
    type,
    title = "",
    content,
    description,
    onClose,
    onSave,
    actions,
    toast,
    markdown,
    loading,
    iconAlignTop,
    borderless,
    hideType = false,
  } = props;
  const { t } = useTranslation();

  const determineIcon = () => {
    if (loading) {
      return (
        <span className={`icon-wrap${!actions ? " flexed" : ""}`}>
          <Spin />
        </span>
      );
    } else if (!hideType) {
      return (
        <span className={`icon-wrap${!actions ? " flexed" : ""}`}>
          <GeneratedNoticeIcon noticeType={type} isToast={toast} />
        </span>
      );
    } else {
      return null;
    }
  };

  return (
    <div
      className={`notice-bar-container ${type} ${toast ? "is-toast" : ""}${iconAlignTop ? " icon-align-top" : ""} ${
        borderless ? "borderless" : ""
      }`}
    >
      <div className="notice-bar">
        {determineIcon()}
        {/* <span className={`icon-wrap${!actions ? " flexed" : ""}`}>
          {loading ? <Spin /> : <GeneratedNoticeIcon noticeType={type} isToast={toast} />}
        </span> */}
        <span className="content-wrap">
          <p className="title">{title}</p>
          {content && (
            <div className="content" style={!actions ? { paddingBottom: "0" } : undefined}>
              {content}
            </div>
          )}

          {markdown && (
            <div>
              <Markdown markdownText={markdown} />
            </div>
          )}
          {description && (
            <div className="description" style={!actions ? { paddingBottom: "0" } : undefined}>
              {description}
            </div>
          )}
          {actions && (
            <span className="action-wrap">
              {actions.secondary && (
                <button className="secondary" onClick={actions.secondary.onClick}>
                  {actions.secondary.label ?? "Learn More"}
                </button>
              )}
              {actions.primary && actions?.primary?.buttonType ? (
                <Button size="xsmall" onClick={actions?.primary?.onClick} type={actions?.primary?.buttonType}>
                  {actions?.primary?.label}
                </Button>
              ) : actions?.primary ? (
                <button className="primary" onClick={actions.primary.onClick}>
                  {actions.primary.label ?? "View Changes "} <FontAwesomeIcon size="sm" icon={faArrowRight} />
                </button>
              ) : null}
            </span>
          )}
        </span>

        <span className={`close-wrap${!actions ? " flexed" : ""}`}>
          {onSave && (
            <Button onClick={onSave} className="notice-save" size="xsmall" type="tertiary">
              {t("Save")}
            </Button>
          )}

          {onClose && (
            <Button onClick={onClose} className="notice-close" type="text" size="small">
              {toast ? t("DISMISS") : <FontAwesomeIcon size="lg" icon={faTimes} />}
            </Button>
          )}
        </span>
      </div>
    </div>
  );
}

const GeneratedNoticeIcon = ({ noticeType, isToast }: { noticeType: NoticeType; isToast: boolean }) => {
  if (isToast) {
    return <FontAwesomeIcon icon={faInfoCircle} />;
  }

  switch (noticeType) {
    case "primary": {
      return <FontAwesomeIcon icon={faInfoCircle} />;
    }
    case "info": {
      return <FontAwesomeIcon icon={faInfoCircle} />;
    }
    case "success": {
      return <FontAwesomeIcon icon={faCircleCheck} />;
    }
    case "error": {
      return <FontAwesomeIcon icon={faInfoCircle} />;
    }
    case "warning": {
      return <FontAwesomeIcon icon={faInfoCircle} />;
    }
    case "alerts": {
      return <FontAwesomeIcon icon={faTriangleExclamation} />;
    }
  }
};
